import type { ProposalDetails } from '@/services/quotes/types';
import { riskProfileCapitalsFingerprint } from './map-api.util';

const lastSeenRiskCapitalsFingerprintByPolicy = new Map<string, string>();

const PENDING_STEP3_RESYNC_KEY_PREFIX = 'capital-global:pending-step3-resync:';

function pendingStep3ResyncStorageKey(policyId: string): string {
  return `${PENDING_STEP3_RESYNC_KEY_PREFIX}${policyId.trim()}`;
}

export function getLastSeenRiskCapitalsFingerprint(policyId: string): string | undefined {
  const policyKey = policyId.trim();
  if (!policyKey) return undefined;
  return lastSeenRiskCapitalsFingerprintByPolicy.get(policyKey);
}

export function markRiskCapitalsFingerprintSeen(
  policyId: string,
  proposalDetails: ProposalDetails | undefined
): void {
  const policyKey = policyId.trim();
  if (!policyKey) return;

  const currentFingerprint = riskProfileCapitalsFingerprint(proposalDetails);
  if (currentFingerprint) {
    lastSeenRiskCapitalsFingerprintByPolicy.set(policyKey, currentFingerprint);
  }
}

export function markPendingStep3Resync(policyId: string): void {
  const policyKey = policyId.trim();
  if (!policyKey || typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(pendingStep3ResyncStorageKey(policyKey), '1');
}

export function hasPendingStep3Resync(policyId: string): boolean {
  const policyKey = policyId.trim();
  if (!policyKey || typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(pendingStep3ResyncStorageKey(policyKey)) === '1';
}

export function clearPendingStep3Resync(policyId: string): void {
  const policyKey = policyId.trim();
  if (!policyKey || typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(pendingStep3ResyncStorageKey(policyKey));
}

/** True when step-2 capitals changed or step 3 has never synced for this policy. */
export function needsResyncFromStep2Capital(
  policyId: string,
  proposalDetails: ProposalDetails | undefined
): boolean {
  const policyKey = policyId.trim();
  if (!policyKey) return false;

  const currentFingerprint = riskProfileCapitalsFingerprint(proposalDetails);
  if (!currentFingerprint) return false;

  if (hasPendingStep3Resync(policyKey)) return true;

  const lastFingerprint = getLastSeenRiskCapitalsFingerprint(policyKey);
  return !lastFingerprint || lastFingerprint !== currentFingerprint;
}
